const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

const vacationPackages = [
  {
    id: 1,
    name: 'Pre/Post-Stay Refresh',
    subtitle: 'Vacation rental apartments',
    badge: 'Most Popular',
    price: '€99',
    period: '/per service',
    description: 'Perfect for vacation rentals between guest stays. Get your property guest-ready quickly and efficiently.',
    features: [
      'Deep cleaning (3–4 hours)',
      'Linen change & laundry',
      'Minor repairs & touch-ups',
      'Restocking essentials (toiletries, basics)',
      'Quick check to make the property guest-ready',
    ],
    ideal: 'Vacation rental owners, Airbnb hosts',
  },
  {
    id: 2,
    name: 'Pre/Post Stay Villa Preparation',
    subtitle: null,
    badge: null,
    price: '€199',
    period: '/per service',
    description: 'Comprehensive preparation service for new listings or seasonal openings. Make a stunning first impression.',
    features: [
      'Interior styling consultation',
      'Complete deep cleaning',
      'Maintenance check (plumbing, electrical, furniture)',
      'Welcome setup & staging (decor, furniture arrangement)',
    ],
    ideal: 'New listings, Seasonal properties',
  },
];

const residentPackages = [
  {
    id: 3,
    name: 'Monthly Home Refresh',
    subtitle: 'Residential Apartment Cleaning',
    badge: 'Best Value',
    price: '€150',
    period: '/month',
    description: 'Perfect for residents who want a hassle-free, clean home every month.',
    features: [
      '2–4 visits / month',
      'Weekly or bi-weekly cleaning visits (based on apartment size)',
      'Basic maintenance check (minor repairs, appliance checks)',
      'Focused on keeping apartments tidy, hygienic, and well-maintained',
    ],
    ideal: 'Residents, Long-term rentals',
  },
];

const premiumAddOn = {
  id: 4,
  name: 'Interior Design & Styling',
  subtitle: 'Optional Premium Add-On',
  badge: null,
  price: 'Custom',
  period: 'Pricing depends on project scope',
  description: 'Professional interior design and styling services to transform your space.',
  features: [
    'Consultation with Art Throng design experts',
    'Full interior styling or small updates for residents or rentals',
    'Integration with cleaning and maintenance schedules for a cohesive service',
    'Optional staging for vacation rentals',
  ],
  ideal: 'Property owners seeking design expertise',
};

const serviceColumns = [
  {
    id: 1,
    icon: 'ri-drop-line',
    title: 'Cleaning Services',
    subtitle: 'Apartments & Villas',
    description: 'Our cleaning service ensures your property is guest-ready, hygienic, and professionally maintained.',
    includes: [
      'Post-stay cleaning for vacation rentals',
      'Deep cleaning of all rooms',
      'Kitchen cleaning (surfaces, appliances, cabinets exterior)',
      'Bathroom sanitization (toilets, showers, sinks, mirrors)',
      'Floor cleaning (sweeping, mopping, vacuuming)',
      'Dusting of furniture and surfaces',
      'Linen change and laundry coordination',
      'Restocking essentials (toilet paper, soap, basic supplies)',
      'Final inspection to ensure guest-ready condition',
    ],
  },
  {
    id: 2,
    icon: 'ri-tools-line',
    title: 'Maintenance Services',
    subtitle: 'Preventive & Corrective',
    description: 'Our maintenance service keeps your property functional, safe, and problem-free, avoiding costly emergency repairs.',
    includes: [
      'Plumbing checks and minor repairs (leaks, taps, drains)',
      'Electrical inspections and small fixes (switches, lights, sockets)',
      'Air-conditioning inspection and filter cleaning',
      'Appliance checks (washing machine, fridge, oven, boiler)',
      'Doors, windows, locks and hinges adjustment',
      'Minor carpentry repairs',
      'Painting touch-ups and wall repairs',
      'General wear-and-tear fixes',
      'Regular property condition checks and reporting',
    ],
  },
  {
    id: 3,
    icon: 'ri-sofa-line',
    title: 'Interior Design & Renovation Services',
    subtitle: null,
    description: 'Our interior design and renovation service enhances your property\'s comfort, aesthetics, and rental value.',
    includes: [
      'Interior design consultation',
      'Space planning and layout optimization',
      'Furniture selection and placement',
      'Lighting recommendations and upgrades',
      'Color palettes and material selection',
      'Styling and staging for vacation rentals',
      'Rental-ready aesthetic upgrades',
      'Seasonal refresh and décor updates',
      'Coordination of renovation works (painting, carpentry, small upgrades)',
    ],
  },
  {
    id: 4,
    icon: 'ri-hammer-line',
    title: 'Renovation',
    subtitle: 'Light to Medium',
    description: 'For upgrades and improvements without full construction.',
    includes: [
      'Painting and wall finishes',
      'Carpentry upgrades (wardrobes, shelving, custom solutions)',
      'Kitchen and bathroom improvements',
      'Flooring upgrades',
      'Functional and aesthetic enhancements',
      'Project coordination with trusted local professionals',
    ],
  },
];

const packagesJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Art Throng Home — Service Packages',
  url: `${siteUrl}/packages`,
  description:
    'Comprehensive service packages for vacation rentals and permanent residents in Goa and Mumbai. Professional cleaning, maintenance, interior design and renovation.',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      item: {
        '@type': 'Offer',
        name: 'Pre/Post-Stay Refresh',
        description:
          'Perfect for vacation rentals between guest stays. Deep cleaning, linen change, minor repairs, restocking essentials.',
        price: '99',
        priceCurrency: 'EUR',
        seller: { '@type': 'Organization', name: 'Art Throng Home' },
        eligibleRegion: ['Goa', 'Mumbai'],
      },
    },
    {
      '@type': 'ListItem',
      position: 2,
      item: {
        '@type': 'Offer',
        name: 'Pre/Post Stay Villa Preparation',
        description:
          'Comprehensive preparation service for new listings or seasonal openings. Interior styling, deep cleaning, maintenance check.',
        price: '199',
        priceCurrency: 'EUR',
        seller: { '@type': 'Organization', name: 'Art Throng Home' },
        eligibleRegion: ['Goa', 'Mumbai'],
      },
    },
    {
      '@type': 'ListItem',
      position: 3,
      item: {
        '@type': 'Offer',
        name: 'Monthly Home Refresh',
        description:
          'Perfect for residents who want a hassle-free, clean home every month. 2–4 visits per month including maintenance checks.',
        price: '150',
        priceCurrency: 'EUR',
        seller: { '@type': 'Organization', name: 'Art Throng Home' },
        eligibleRegion: ['Goa', 'Mumbai'],
      },
    },
    {
      '@type': 'ListItem',
      position: 4,
      item: {
        '@type': 'Offer',
        name: 'Interior Design & Styling',
        description:
          'Professional interior design and styling services to transform your space. Custom pricing based on project scope.',
        seller: { '@type': 'Organization', name: 'Art Throng Home' },
        eligibleRegion: ['Goa', 'Mumbai'],
      },
    },
  ],
};

export default function PackagesPage() {
  const scrollToBooking = () => {
    window.REACT_APP_NAVIGATE('/');
    setTimeout(() => {
      const element = document.getElementById('booking');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(packagesJsonLd) }}
      />
      {/* Hero Section */}
      <section className="py-20 bg-[#F5F1ED]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="font-serif text-5xl md:text-6xl text-[#2B2B2B] mb-6">
            Service Packages
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive service solutions designed for vacation rentals and permanent residents. Professional care for every aspect of your property.
          </p>
        </div>
      </section>

      {/* Service Packages Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {serviceColumns.map((service) => (
              <div
                key={service.id}
                className="bg-white border-2 border-gray-200 rounded-3xl p-8 hover:border-[#8B9B7E] hover:shadow-xl transition-all"
              >
                <div className="flex items-center justify-center w-16 h-16 bg-[#8B9B7E]/10 rounded-2xl mb-6">
                  <i className={`${service.icon} text-3xl text-[#8B9B7E]`}></i>
                </div>

                <h3 className="text-2xl font-bold text-[#2B2B2B] mb-2">
                  {service.title}
                </h3>
                
                {service.subtitle && (
                  <p className="text-sm text-gray-500 mb-4">{service.subtitle}</p>
                )}

                <p className="text-gray-600 mb-6 leading-relaxed">
                  {service.description}
                </p>

                <div className="mb-6">
                  <h4 className="font-semibold text-[#2B2B2B] mb-3">Includes:</h4>
                  <ul className="space-y-2">
                    {service.includes.map((item, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <i className="ri-checkbox-circle-fill text-[#8B9B7E] text-lg flex-shrink-0 mt-0.5"></i>
                        <span className="text-sm text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={scrollToBooking}
                  className="w-full bg-[#8B9B7E] text-white py-3 rounded-lg font-semibold hover:bg-[#7a8a6d] transition-all whitespace-nowrap cursor-pointer"
                >
                  Book Service
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pre-Stay & Post-Stay Cleaning */}
      <section className="py-20 bg-[#F5F1ED]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="font-serif text-4xl text-[#2B2B2B] mb-4">
              Pre-Stay & Post-Stay Cleaning
            </h2>
            <p className="text-lg text-gray-600">
              Specialized packages for vacation rentals to keep your property guest-ready
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {vacationPackages.map((pkg) => (
              <div
                key={pkg.id}
                className="bg-white border-2 border-gray-200 rounded-3xl p-10 hover:border-[#8B9B7E] hover:shadow-xl transition-all"
              >
                {pkg.badge && (
                  <div className="inline-block bg-[#8B9B7E] text-white text-xs font-semibold px-4 py-2 rounded-full mb-6">
                    {pkg.badge}
                  </div>
                )}

                <h3 className="text-3xl font-bold text-[#2B2B2B] mb-2">
                  {pkg.name}
                </h3>
                
                {pkg.subtitle && (
                  <p className="text-sm text-gray-500 mb-4">{pkg.subtitle}</p>
                )}

                <div className="mb-6">
                  <span className="text-5xl font-bold text-[#2B2B2B]">
                    {pkg.price}
                  </span>
                  <span className="text-gray-500 text-sm ml-2">
                    {pkg.period}
                  </span>
                </div>

                <p className="text-gray-600 mb-6 leading-relaxed">
                  {pkg.description}
                </p>

                <ul className="space-y-3 mb-6">
                  {pkg.features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <i className="ri-checkbox-circle-fill text-[#8B9B7E] text-xl flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="bg-[#F5F1ED] rounded-lg p-4 mb-6">
                  <p className="text-sm text-gray-600">
                    <strong>Ideal for:</strong> {pkg.ideal}
                  </p>
                </div>

                <button
                  onClick={scrollToBooking}
                  className="w-full bg-[#8B9B7E] text-white py-4 rounded-lg font-semibold hover:bg-[#7a8a6d] transition-all whitespace-nowrap cursor-pointer"
                >
                  Select Package
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Monthly / Recurring Maintenance */}
      <section className="py-20 bg-[#F5F1ED]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="font-serif text-4xl text-[#2B2B2B] mb-4">
              Monthly / Recurring Maintenance
            </h2>
            <p className="text-lg text-gray-600">
              Regular cleaning for residents and long-term rentals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-1 max-w-2xl mx-auto gap-8">
            {residentPackages.map((pkg) => (
              <div
                key={pkg.id}
                className="bg-white rounded-3xl p-10 hover:shadow-xl transition-all"
              >
                {pkg.badge && (
                  <div className="inline-block bg-[#8B9B7E] text-white text-xs font-semibold px-4 py-2 rounded-full mb-6">
                    {pkg.badge}
                  </div>
                )}

                <h3 className="text-3xl font-bold text-[#2B2B2B] mb-2">
                  {pkg.name}
                </h3>
                
                {pkg.subtitle && (
                  <p className="text-sm text-gray-500 mb-4">{pkg.subtitle}</p>
                )}

                <div className="mb-6">
                  <span className="text-5xl font-bold text-[#2B2B2B]">
                    {pkg.price}
                  </span>
                  <span className="text-gray-500 text-sm ml-2">
                    {pkg.period}
                  </span>
                </div>

                <p className="text-gray-600 mb-6 leading-relaxed">
                  {pkg.description}
                </p>

                <ul className="space-y-3 mb-6">
                  {pkg.features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <i className="ri-checkbox-circle-fill text-[#8B9B7E] text-xl flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="bg-[#F5F1ED] rounded-lg p-4 mb-6">
                  <p className="text-sm text-gray-600">
                    <strong>Ideal for:</strong> {pkg.ideal}
                  </p>
                </div>

                <button
                  onClick={scrollToBooking}
                  className="w-full bg-[#8B9B7E] text-white py-4 rounded-lg font-semibold hover:bg-[#7a8a6d] transition-all whitespace-nowrap cursor-pointer"
                >
                  Select Package
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interior Design / Styling Add-On */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="font-serif text-4xl text-[#2B2B2B] mb-4">
              Interior Design / Styling
            </h2>
            <p className="text-lg text-gray-600">
              Optional premium add-on for complete transformation
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            <div className="bg-gradient-to-br from-[#2B2B2B] to-[#3d3d3d] rounded-3xl p-10 text-white hover:shadow-2xl transition-all">
              <h3 className="text-3xl font-bold mb-2">
                {premiumAddOn.name}
              </h3>
              
              {premiumAddOn.subtitle && (
                <p className="text-sm text-gray-300 mb-4">{premiumAddOn.subtitle}</p>
              )}

              <div className="mb-6">
                <span className="text-5xl font-bold">
                  {premiumAddOn.price}
                </span>
                <p className="text-gray-300 text-sm mt-2">
                  {premiumAddOn.period}
                </p>
              </div>

              <p className="text-gray-200 mb-6 leading-relaxed">
                {premiumAddOn.description}
              </p>

              <ul className="space-y-3 mb-6">
                {premiumAddOn.features.map((feature, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <i className="ri-checkbox-circle-fill text-[#8B9B7E] text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-200">{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="bg-white/10 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-200">
                  <strong>Ideal for:</strong> {premiumAddOn.ideal}
                </p>
              </div>

              <button
                onClick={scrollToBooking}
                className="w-full bg-[#8B9B7E] text-white py-4 rounded-lg font-semibold hover:bg-[#7a8a6d] transition-all whitespace-nowrap cursor-pointer"
              >
                Request Consultation
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Custom Package CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="bg-[#F5F1ED] rounded-3xl p-12 shadow-lg">
            <h2 className="font-serif text-4xl text-[#2B2B2B] mb-6">
              Need a Custom Package?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              We can create a tailored solution that perfectly fits your specific needs and budget
            </p>
            <button
              onClick={scrollToBooking}
              className="bg-[#8B9B7E] text-white px-10 py-5 rounded-lg font-semibold hover:bg-[#7a8a6d] transition-all text-lg whitespace-nowrap cursor-pointer"
            >
              Contact Us
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
